import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Film, Clapperboard } from "lucide-react";

import { HeroCinematic } from "@/components/HeroCinematic";
import { Marquee } from "@/components/Marquee";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { ContactSection } from "@/components/ContactSection";
import { VideoModal } from "@/components/VideoModal";
import { STATS, IMAGES, BRAND, BRANDS_WORKED_WITH } from "@/lib/siteData";

const AD_FILMS = [
  {
    title: "Advaitya Projects — TVC",
    videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_ad-films-pro/artifacts/e8006d01c727cac6_VIDEO-2026-10-22-07-18-35.mp4",
    thumbnail: "/posters/adfilm-2.jpg",
    description: "A television commercial for Advaitya Projects — crafted end-to-end from concept to final cut.",
  },
  {
    title: "Proofer Chemical",
    videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_ad-films-pro/artifacts/f6532bf183d5a0fc_VID-20261017-WA0017.mp4",
    thumbnail: "/posters/latest-adfilm.jpg",
    description: "Our latest advertising film for Proofer Chemical — a cinematic campaign crafted end-to-end from concept to final cut.",
  },
  {
    title: "Darshan Incense's Black Stone 3 in One Agarbatti",
    videoUrl: "https://drive.google.com/file/d/19JRI3mHcK1veCBko-p1-JCORiPawklj2/view?usp=drive_link",
    thumbnail: "https://customer-assets-cm19k8pv.emergentagent.net/job_ad-films-pro/artifacts/4je88kem_image.png",
    description: "Our advertising film for Darshan Incense — the Black Stone 3 in One Agarbatti, crafted end-to-end from concept to final cut.",
  },
];

export default function Home() {
  const [activeAd, setActiveAd] = useState(null);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) (window.__lenis?.scrollTo?.(el, { offset: -80 })) || el.scrollIntoView();
      }, 600);
    }
  }, []);

  return (
    <div>
      <div id="home"><HeroCinematic /></div>

      {/* About */}
      <section id="about" className="py-16 md:py-20 container-x scroll-mt-24" data-testid="about-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className="relative">
            <div className="relative overflow-hidden aspect-[4/3]">
              <img src={IMAGES.stairwellFilm} alt="Film production" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-void/60 to-transparent" />
            </div>
          </Reveal>
          <div>
            <p className="text-xs md:text-sm tracking-[0.35em] uppercase font-bold text-gold mb-5">About Us</p>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-offwhite">
                Where Ideas Become <span className="italic text-gold">Powerful Visual Stories</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-white/60 font-light leading-relaxed text-base md:text-lg">
                Founded in 2016, Vivek Parekh Production is a Mumbai-based creative production house bringing
                stories, brands, and entertainment together.
              </p>
              <p className="mt-5 text-white/60 font-light leading-relaxed text-base md:text-lg">
                From movie associations and ad films to celebrity collaborations, we turn ambitious ideas into
                impactful productions.
              </p>
            </Reveal>
            <StaggerGroup className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {STATS.map((s) => (
                <StaggerItem key={s.label} className="bg-void p-6 text-center">
                  <p className="font-display text-4xl text-gold leading-none">{s.value}</p>
                  <p className="mt-2 text-[0.65rem] uppercase tracking-[0.2em] text-white/50">{s.label}</p>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 md:py-20 container-x scroll-mt-24" data-testid="services-section">
        <SectionHeading eyebrow="What We Do" title={"Two Ways\nWe Elevate Brands"} className="mb-14" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Movie Integration */}
          <Reveal className="group relative overflow-hidden border border-white/10 min-h-[440px] flex flex-col justify-between">
            <img src={IMAGES.stairwellFilm} alt="Movie integration" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-void/40" />
            <div className="relative p-8">
              <Film className="text-gold" size={28} />
              <span className="block mt-6 font-display text-5xl text-gold/40">120+</span>
            </div>
            <div className="relative p-8">
              <h3 className="font-display text-4xl uppercase tracking-wide text-offwhite leading-none">Brand Integration</h3>
              <p className="mt-4 text-white/60 font-light text-sm leading-relaxed">In-film & out-film brand integrations, song placements and promotional partnerships across 120+ film associations.</p>
              <Link to="/movie-integrations" data-testid="view-associations-link" className="mt-6 inline-flex items-center gap-2 bg-gold text-void px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-champagne transition-colors">
                View All Associations <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>

          {/* Ad Film */}
          <Reveal delay={0.1} className="group relative overflow-hidden border border-white/10 min-h-[440px] flex flex-col justify-between">
            <img src={AD_FILMS[0].thumbnail} alt="Ad film production" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-void/40" />
            <div className="relative p-8">
              <Clapperboard className="text-gold" size={28} />
            </div>
            <div className="relative p-8">
              <h3 className="font-display text-4xl uppercase tracking-wide text-offwhite leading-none">Ad Films</h3>
              <p className="mt-4 text-white/60 font-light text-sm leading-relaxed">Advertising films crafted end-to-end — from concept and scripting to shoot and final cut.</p>
              <a href="#ad-films" data-testid="view-adfilms-link" className="mt-6 inline-flex items-center gap-2 border border-gold text-gold px-6 py-3 uppercase tracking-[0.2em] text-xs hover:bg-gold hover:text-void transition-colors">
                <Play size={14} fill="currentColor" /> View Ad Films
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <VideoModal open={!!activeAd} url={activeAd?.videoUrl} title={activeAd?.title} onClose={() => setActiveAd(null)} />

      {/* Ad Films showcase */}
      <section id="ad-films" className="py-16 md:py-20 container-x scroll-mt-24" data-testid="ad-films-section">
        <SectionHeading eyebrow="Ad Films" title={"Campaigns We've\nBrought to Life"} className="mb-14" />
        <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {AD_FILMS.map((film, i) => (
            <StaggerItem key={film.title}>
              <button
                onClick={() => setActiveAd(film)}
                data-testid={`ad-film-card-${i}`}
                aria-label={`Play ${film.title}`}
                className="group relative w-full overflow-hidden border border-white/10 aspect-video text-left"
              >
                <img src={film.thumbnail} alt={film.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-void via-void/55 to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 rounded-full border border-gold/70 bg-void/40 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:bg-gold group-hover:scale-110">
                    <Play size={24} className="text-gold group-hover:text-void ml-1 transition-colors" fill="currentColor" />
                  </span>
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide text-offwhite leading-none">{film.title}</h3>
                  <p className="mt-2 text-white/60 font-light text-sm leading-relaxed line-clamp-2">{film.description}</p>
                </div>
              </button>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>


      {/* Brands — placed just above the contact form */}
      <section id="brands" className="scroll-mt-24" data-testid="brands-section">
        <div className="pt-16 md:pt-20 pb-6 text-center container-x">
          <p className="text-[0.6rem] md:text-xs uppercase tracking-[0.4em] text-gold" data-testid="brands-heading">Brands that trust us</p>
        </div>
        <Marquee items={BRANDS_WORKED_WITH} />
        <div className="pt-8 md:pt-10 pb-2 text-center container-x">
          <Link
            to="/brands"
            data-testid="see-all-brands-btn"
            className="inline-flex items-center gap-2 border border-gold text-gold px-8 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-gold hover:text-void transition-colors"
          >
            See All <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
